/*
  Product peek — náhled produktu pro odkazy vložené V TEXTU.

  Historie a proč to takhle vypadá
  --------------------------------
  Modal vznikl jen pro blog: inline `<script>` v `blocks/_blog-post-content.liquid`, natvrdo
  `document.querySelector('.blog-post-content')` a listener přilepený na každý odkaz při
  načtení stránky. Na PDP se ten samý obsah (migrované HTML s odkazy na jiné produkty) žije
  v jiných sekcích, takže se funkce nedala použít. 2026-09-11 vytaženo sem — beze změny
  vzhledu a beze změny chování na blogu.

  Dvě věci se proti původní verzi zobecnily:

  1. **Kde to platí, určuje značka `data-product-peek-root`** na kontejneru s rich textem
     (blog článek, popis produktu, „jak používat"). Ne CSS třída konkrétní šablony. Modal
     se tak NIKDY nepověsí na produktové karty / doporučené produkty, kde musí zůstat
     obyčejná navigace.
  2. **Jeden delegovaný listener na `document`** místo listeneru na každém odkazu. Horizon
     sekce se překreslují (morph / section rendering) — po překreslení by odkazy z původní
     verze zůstaly mrtvé, protože jejich listener zmizel s DOM uzlem.

  Data: `/products/<handle>.js` (Ajax API). Formát ceny se odvozuje ze dvou hotových vzorků
  `| money` vyrenderovaných serverem (viz `snippets/product-peek.liquid`) — `Intl` se
  `style: 'currency'` psal „56,00 Kč" tam, kde zbytek webu píše „56 Kč".

  Add-to-cart a množství řeší sdílený `window.TBCartQty` (`assets/tb-cart-qty.js`), stejně
  jako v původní blogové verzi.
*/
(() => {
  const dialog = document.querySelector('[data-product-peek-dialog]');
  if (!dialog) return;

  const dialogContent = dialog.querySelector('[data-product-peek-content]');
  const closeButton = dialog.querySelector('[data-product-peek-close]');

  let labels = {};
  let moneySamples = {};
  try {
    const configNode = document.querySelector('[data-product-peek-config]');
    const config = configNode ? JSON.parse(configNode.textContent || '{}') : {};
    labels = config.labels || {};
    moneySamples = config.money || {};
  } catch (error) {
    labels = {};
  }

  const productCache = new Map();
  const doprodejCache = new Map();
  let lastFocusedElement = null;

  document.addEventListener('click', handleDocumentClick);
  dialog.addEventListener('click', handleDialogBackdropClick);
  dialog.addEventListener('keydown', handleDialogKeydown);
  dialog.addEventListener('close', handleDialogClosed);
  closeButton?.addEventListener('click', closeDialog);

  /**
   * Musí přežít i to, že uživatel klikne na `<span>`/`<ins>` uvnitř odkazu — migrované HTML
   * je plné vnořených obalů, proto `closest('a[href]')`, ne `event.target.tagName`.
   */
  function handleDocumentClick(event) {
    if (!isPlainLeftClick(event)) return;

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link) return;
    if (!link.closest('[data-product-peek-root]')) return;

    // Standardní chování prohlížeče tam, kde ho autor odkazu explicitně chtěl.
    if (link.target && link.target !== '_self') return;
    if (link.hasAttribute('download')) return;

    const handle = getProductHandle(link);
    if (!handle) return;

    event.preventDefault();
    openForHandle(handle, link.href);
  }

  /** Cmd/Ctrl/Shift/Alt + klik a prostřední tlačítko = nechat na prohlížeči (nová záložka…). */
  function isPlainLeftClick(event) {
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
  }

  function getProductHandle(link) {
    const href = link.getAttribute('href');
    if (!href) return '';

    let url;
    try {
      url = new URL(href, window.location.origin);
    } catch (error) {
      return '';
    }

    // Odkaz mimo tenhle shop se nikdy neotvírá v modalu.
    if (url.origin !== window.location.origin) return '';

    const match = url.pathname.match(/\/products\/([^/?#]+)/);
    if (!match) return '';

    const handle = decodeURIComponent(match[1]);
    // Nevyřešený migrační placeholder (`{{OLD_URL:…}}`) není handle — takový odkaz
    // ať klidně skončí na 404, aby se chyba v datech neschovala za prázdný modal.
    if (handle.includes('{{') || handle.includes('}}')) return '';
    return handle;
  }

  async function openForHandle(handle, fallbackUrl) {
    const [product, doprodejInfo] = await Promise.all([fetchProduct(handle), fetchDoprodejInfo(handle)]);
    if (!product) {
      window.location.href = fallbackUrl;
      return;
    }
    openDialog(product, fallbackUrl, doprodejInfo);
  }

  /**
   * GH#3: blok „doprodej info" (metapole `custom.doprodej_info`) v Ajax API `.js` není.
   * Vykreslí ho server stejným snippetem jako na PDP (`sections/doprodej-info.liquid`).
   * Prázdný řetězec = produkt doprodej nemá nebo se fetch nepovedl (náhled funguje dál bez něj).
   */
  async function fetchDoprodejInfo(handle) {
    if (doprodejCache.has(handle)) return doprodejCache.get(handle);

    try {
      const root = String(window.Shopify?.routes?.root || '/').replace(/\/$/, '');
      const response = await fetch(`${root}/products/${encodeURIComponent(handle)}?section_id=doprodej-info`);
      if (!response.ok) return '';

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const block = html.querySelector('.doprodej-info');
      const markup = block ? block.outerHTML : '';
      doprodejCache.set(handle, markup);
      return markup;
    } catch (error) {
      return '';
    }
  }

  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);

    try {
      // `routes.root` je `/` na CZ trhu a `/xx` na lokalizovaném — bez něj by Ajax API
      // na jiném locale vracelo 404.
      const root = String(window.Shopify?.routes?.root || '/').replace(/\/$/, '');
      const response = await fetch(`${root}/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return null;

      const product = await response.json();
      productCache.set(handle, product);
      return product;
    } catch (error) {
      return null;
    }
  }

  function renderProduct(target, product, productUrl, doprodejInfo = '') {
    if (!target) return;

    const selectedVariant = getAvailableVariant(product);
    const image = product.featured_image || product.images?.[0] || selectedVariant?.featured_image?.src || '';
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variantSelectId = `ProductPeekVariant-${product.id}`;
    const description = getProductDescription(product);

    target.innerHTML = `
      ${
        image
          ? `<img class="product-peek__image" src="${escapeAttribute(image)}" alt="${escapeAttribute(
              product.title || '',
            )}" width="200" height="200" loading="lazy" fetchpriority="low">`
          : ''
      }
      <div class="product-peek__content">
        <h3 class="product-peek__title">
          <a class="product-peek__title-link" href="${escapeAttribute(productUrl)}">${escapeHtml(product.title || '')}</a>
        </h3>
        <div class="product-peek__price" data-product-peek-price>${formatPrice(selectedVariant?.price ?? product.price)}</div>
        ${doprodejInfo}
        ${description ? `<p class="product-peek__description">${escapeHtml(description)}</p>` : ''}
      </div>
      <div class="product-peek__actions">
        ${
          variants.length > 1
            ? `<div class="product-peek__variant">
                <label class="product-peek__variant-label" for="${variantSelectId}">${escapeHtml(labels.variant || '')}</label>
                <select class="product-peek__select" id="${variantSelectId}" data-product-peek-variant>
                  ${variants
                    .map(
                      (variant) =>
                        `<option value="${variant.id}" data-price="${variant.price}" ${
                          variant.available === false ? 'disabled' : ''
                        } ${selectedVariant?.id === variant.id ? 'selected' : ''}>${escapeHtml(variant.title)}</option>`,
                    )
                    .join('')}
                </select>
              </div>`
            : ''
        }
        <div
          class="tb-qty-ctrl product-peek__qty"
          data-variant-id="${escapeAttribute(selectedVariant?.id || '')}"
          data-product-id="${escapeAttribute(product.id || '')}"
          data-add-label="${escapeAttribute(labels.addToCart || '')}"
        >
          <div class="tb-qty-ctrl__stepper">
            <button type="button" class="tb-qty-ctrl__dec" aria-label="${escapeAttribute(labels.decrease || '')}" hidden>−</button>
            <span class="tb-qty-ctrl__num" hidden>0</span>
            <button type="button" class="tb-qty-ctrl__inc" aria-label="${escapeAttribute(labels.increase || '')}" ${
              selectedVariant?.available === false ? 'disabled' : ''
            }>+</button>
          </div>
        </div>
        <a class="product-peek__link" href="${escapeAttribute(productUrl)}">${escapeHtml(labels.viewProduct || '')} →</a>
      </div>
    `;

    const select = target.querySelector('[data-product-peek-variant]');
    const price = target.querySelector('[data-product-peek-price]');
    const qtyControl = target.querySelector('.product-peek__qty');
    const increaseButton = qtyControl?.querySelector('.tb-qty-ctrl__inc');

    select?.addEventListener('change', () => {
      const variant = variants.find((item) => String(item.id) === select.value);
      if (price) price.textContent = formatPrice(variant?.price ?? product.price);
      if (qtyControl && variant?.id) qtyControl.dataset.variantId = String(variant.id);
      if (increaseButton) increaseButton.disabled = variant?.available === false;
      window.TBCartQty?.renderAll();
    });

    window.TBCartQty?.renderAll();
  }

  function getAvailableVariant(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    return variants.find((variant) => variant.available !== false) || variants[0] || null;
  }

  function getProductDescription(product) {
    const source = product.description || product.body_html || '';
    const text = stripHtml(source).replace(/\s+/g, ' ').trim();
    if (!text) return '';

    return text.length > 115 ? `${text.slice(0, 112).trim()}…` : text;
  }

  function openDialog(product, productUrl, doprodejInfo = '') {
    lastFocusedElement = document.activeElement;
    renderProduct(dialogContent, product, productUrl, doprodejInfo);
    // Otevře-li se modal nad už otevřeným modalem (odkaz uvnitř modalu není, ale
    // showModal() na otevřeném dialogu vyhodí výjimku), překreslí se jen obsah.
    if (!dialog.open) dialog.showModal();
    focusFirstDialogElement();
  }

  function closeDialog() {
    if (dialog.open) dialog.close();
  }

  function handleDialogClosed() {
    if (lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus();
  }

  function handleDialogBackdropClick(event) {
    if (event.target === dialog) closeDialog();
  }

  /**
   * Escape: nativní `<dialog>` by se zavřel sám, JENŽE cookie lišta (Pandectes,
   * `pandectes-core-latest.js`) má na `document` globální keydown listener, který na
   * Escape volá `preventDefault()` — a tím nativní zavření vypne pro KAŽDÝ dialog na webu
   * (ověřeno stack trace v prohlížeči 2026-09-11). Náš listener sedí na dialogu, tedy
   * v cestě události dřív než ten na dokumentu, takže Escape zavře modal bez ohledu na app.
   *
   * Tab: kroužení fokusu uvnitř modalu.
   */
  function handleDialogKeydown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      closeDialog();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = getDialogFocusableElements();
    if (!focusableElements.length) return;

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function focusFirstDialogElement() {
    const first = getDialogFocusableElements()[0];
    first?.focus();
  }

  function getDialogFocusableElements() {
    return [...dialog.querySelectorAll('a[href], button:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')];
  }

  // Zdrojem pravdy pro tvar ceny je server. `Intl` se `style: 'currency'` se řídí vlastní
  // locale tabulkou, takže psal „56,00 Kč" tam, kde zbytek webu píše „56 Kč" — dva zápisy
  // téže ceny na jedné stránce. Dva hotové vzorky `| money` jsou navíc presentment-aware
  // (na SK trhu přijdou EUR pravidla samy). Stejný postup používají
  // `assets/gift-progress-bar.js` a `assets/free-shipping-bar.js` (regression-log 2026-06-22).
  //
  // Pozn. 2026-09-14: dřív tu stálo, že `shop.money_format` je „natvrdo formát obchodu".
  // Není — v Liquidu se řídí trhem požadavku (změřeno na royalbay.sk). Vzorky jsou stejně
  // praktičtější (odpadá osm variant placeholderu), ale ne proto, že by ten druhý byl chybný.
  const moneyShape = (() => {
    const group = String(moneySamples.group || '');
    const decimal = String(moneySamples.decimal || '');
    if (!group || !decimal) return null;

    const keep = /[^\d.,\s']/g;
    const grpNum = group.replace(keep, '').trim();
    const decNum = decimal.replace(keep, '').trim();

    let decimals = 0;
    let decSep = '';
    const dec = decNum.match(/^\d+(\D)(\d+)$/);
    if (dec) {
      decSep = dec[1];
      decimals = dec[2].length;
    }

    let grpInt = grpNum;
    if (decimals > 0 && grpNum.length > decimals + 1) grpInt = grpNum.slice(0, grpNum.length - decimals - 1);
    const grp = grpInt.match(/\d+(\D)\d{3}/);

    const digits = decimal.match(/\d(?:[\d.,\s']*\d)?/);
    if (!digits) return null;

    return {
      decimals,
      decSep,
      groupSep: grp ? grp[1] : '',
      prefix: decimal.slice(0, digits.index),
      suffix: decimal.slice(digits.index + digits[0].length),
    };
  })();

  function formatPrice(value) {
    const amount = Number(value || 0) / 100;
    if (!moneyShape) {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: moneySamples.currency || 'CZK',
      }).format(amount);
    }

    const fixed = amount.toFixed(moneyShape.decimals);
    const [rawInt, decPart] = fixed.split('.');
    const intPart = moneyShape.groupSep
      ? rawInt.replace(/\B(?=(\d{3})+(?!\d))/g, moneyShape.groupSep)
      : rawInt;
    const number = moneyShape.decimals > 0 ? intPart + moneyShape.decSep + decPart : intPart;
    return moneyShape.prefix + number + moneyShape.suffix;
  }

  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  function escapeAttribute(value) {
    return escapeHtml(value).replace(/"/g, '&quot;');
  }

  function stripHtml(value) {
    const div = document.createElement('div');
    div.innerHTML = value;
    return div.textContent || '';
  }
})();
